import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getVenue, updateVenue, deleteVenue } from "@/api/admin.api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ArrowLeft, Trash2, Save } from "lucide-react";

export function VenueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "venue", id],
    queryFn: () => getVenue(id!),
    enabled: !!id,
  });

  const [planType, setPlanType] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean | null>(null);
  // null = "kullanıcı henüz dokunmadı, data?.aiCredits göster"; sayı = elle girilen
  // değer. Undefined yerine null ki controlled input'a "" düşmesin.
  const [aiCredits, setAiCredits] = useState<number | null>(null);
  // Yemeksepeti beta gate — sadece admin açar, açılınca restoran dashboard'unda
  // yeni "Yemeksepeti" sekmesi belirir.
  const [yemeksepetiEnabled, setYemeksepetiEnabled] = useState<boolean | null>(null);
  // Trendyol GO beta gate — aynı Yemeksepeti deseni; açılınca restoran
  // dashboard'unda "Trendyol GO" sekmesi belirir.
  const [trendyolgoEnabled, setTrendyolgoEnabled] = useState<boolean | null>(null);

  const currentPlan = planType || data?.planType || "FREE";
  const currentActive = isActive ?? data?.isActive ?? true;
  const currentCredits = aiCredits ?? data?.aiCredits ?? 0;
  const currentYemeksepeti = yemeksepetiEnabled ?? data?.yemeksepetiEnabled ?? false;
  const currentTrendyolgo = trendyolgoEnabled ?? data?.trendyolgoEnabled ?? false;

  const updateMutation = useMutation({
    mutationFn: (updates: Record<string, unknown>) => updateVenue(id!, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "venue", id] }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVenue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "venues"] });
      navigate("/venues");
    },
  });

  const handleSave = () => {
    const updates: Record<string, unknown> = {};
    if (planType && planType !== data?.planType) updates.planType = planType;
    if (isActive !== null && isActive !== data?.isActive) updates.isActive = isActive;
    if (aiCredits !== null && aiCredits !== data?.aiCredits) updates.aiCredits = aiCredits;
    if (yemeksepetiEnabled !== null && yemeksepetiEnabled !== data?.yemeksepetiEnabled) {
      updates.yemeksepetiEnabled = yemeksepetiEnabled;
    }
    if (trendyolgoEnabled !== null && trendyolgoEnabled !== data?.trendyolgoEnabled) {
      updates.trendyolgoEnabled = trendyolgoEnabled;
    }
    if (Object.keys(updates).length > 0) {
      updateMutation.mutate(updates, {
        // Başarılı kaydetme sonrası local edit state'i reset → ekran data'dan
        // tekrar okur, "kaydedilmedi" badge'i yanıltıcı durmaz.
        onSuccess: () => {
          setPlanType("");
          setIsActive(null);
          setAiCredits(null);
          setYemeksepetiEnabled(null);
          setTrendyolgoEnabled(null);
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="mb-6 h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-muted-foreground">Isletme bulunamadi</p>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/venues")}>
          <ArrowLeft className="size-5" />
        </Button>
        <h1 className="text-2xl font-bold">{data.name}</h1>
        <Badge variant="secondary">{data.planType}</Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Isletme Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.logo && (
              <img src={data.logo} alt="" className="size-20 rounded-xl object-cover" />
            )}
            <div>
              <p className="text-sm text-muted-foreground">Slug</p>
              <p className="font-medium">/{data.slug}</p>
            </div>
            {data.address && (
              <div>
                <p className="text-sm text-muted-foreground">Adres</p>
                <p className="font-medium">{data.address}</p>
              </div>
            )}
            {data.phone && (
              <div>
                <p className="text-sm text-muted-foreground">Telefon</p>
                <p className="font-medium">{data.phone}</p>
              </div>
            )}
            {data.owner && (
              <div>
                <p className="text-sm text-muted-foreground">Sahip</p>
                <p className="font-medium">{data.owner.name}</p>
                <p className="text-sm text-muted-foreground">{data.owner.email}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Plan Yonetimi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Plan Tipi</Label>
              <Select value={currentPlan} onValueChange={(v) => setPlanType(v ?? "FREE")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FREE">FREE</SelectItem>
                  <SelectItem value="PRO">PRO</SelectItem>
                  <SelectItem value="BUSINESS">BUSINESS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label>Aktif</Label>
              <Switch
                checked={currentActive}
                onCheckedChange={setIsActive}
              />
            </div>

            {/* AI Kredisi — admin elle ekleyebilir/çıkarabilir. Müşteri Pro
                ödediği halde kredisi düştüyse ya da promo veriyorsak hızlı
                +50/+100/+500 butonları, ayrıca direkt input ile set. */}
            <div className="space-y-2">
              <Label>AI Kredisi</Label>
              <div className="flex items-stretch gap-2">
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={currentCredits}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setAiCredits(Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0);
                  }}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAiCredits(0)}
                  title="Sıfırla"
                >
                  0
                </Button>
              </div>
              <div className="flex gap-1.5">
                {[50, 100, 500, 1000].map((delta) => (
                  <Button
                    key={delta}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => setAiCredits(currentCredits + delta)}
                  >
                    +{delta}
                  </Button>
                ))}
              </div>
              {aiCredits !== null && aiCredits !== data?.aiCredits && (
                <p className="text-xs text-amber-600">
                  Değişiklik var: {data?.aiCredits ?? 0} → {aiCredits}. Kaydet'e bas.
                </p>
              )}
            </div>

            {/* Yemeksepeti entegrasyonu — beta/pilot tier. Açılınca restoran
                dashboard'unda yeni sekme belirir. Restoran sahibi tarafından
                değil sadece buradan açılabilir. */}
            <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/30">
              <div>
                <Label>Yemeksepeti Entegrasyonu</Label>
                <p className="text-xs text-muted-foreground">Beta — sadece pilot restoranlar</p>
              </div>
              <Switch
                checked={currentYemeksepeti}
                onCheckedChange={(v) => setYemeksepetiEnabled(v)}
              />
            </div>
            {yemeksepetiEnabled !== null && yemeksepetiEnabled !== !!data?.yemeksepetiEnabled && (
              <p className="text-xs text-amber-600">
                Değişiklik var: {data?.yemeksepetiEnabled ? "Açık" : "Kapalı"} → {yemeksepetiEnabled ? "Açık" : "Kapalı"}. Kaydet'e bas.
              </p>
            )}

            {/* Trendyol GO entegrasyonu — beta/pilot tier, aynı Yemeksepeti
                deseni. Sadece buradan açılır. */}
            <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/30">
              <div>
                <Label>Trendyol GO Entegrasyonu</Label>
                <p className="text-xs text-muted-foreground">Beta — sadece pilot restoranlar</p>
              </div>
              <Switch
                checked={currentTrendyolgo}
                onCheckedChange={(v) => setTrendyolgoEnabled(v)}
              />
            </div>
            {trendyolgoEnabled !== null && trendyolgoEnabled !== !!data?.trendyolgoEnabled && (
              <p className="text-xs text-amber-600">
                Değişiklik var: {data?.trendyolgoEnabled ? "Açık" : "Kapalı"} → {trendyolgoEnabled ? "Açık" : "Kapalı"}. Kaydet'e bas.
              </p>
            )}

            <Button onClick={handleSave} className="w-full gap-2" disabled={updateMutation.isPending}>
              <Save className="size-4" />
              {updateMutation.isPending ? "Kaydediliyor..." : "Kaydet"}
            </Button>

            <ConfirmDialog
              trigger={
                <Button variant="destructive" className="w-full gap-2">
                  <Trash2 className="size-4" />
                  Isletmeyi Sil
                </Button>
              }
              title="Isletmeyi Sil"
              description="Bu isletmeyi ve tum verilerini silmek istediginizden emin misiniz? Bu islem geri alinamaz."
              confirmLabel="Sil"
              onConfirm={() => deleteMutation.mutate(id!)}
            />
          </CardContent>
        </Card>

        {/* QR Codes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">QR Kodlar ({data.qrCodes.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {data.qrCodes.length === 0 ? (
              <p className="text-sm text-muted-foreground">QR kod yok</p>
            ) : (
              <div className="space-y-2">
                {data.qrCodes.map((qr) => (
                  <div key={qr._id} className="rounded-lg border p-3 text-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{qr.label || "QR Kod"}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(qr.createdAt).toLocaleDateString("tr-TR")}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      heremenu.app/q/{qr.shortUrl}
                    </div>
                    {qr.style?.fgColor && (
                      <div className="text-xs">Renk: {qr.style.fgColor}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Loyalty Program */}
      {data.loyaltyProgram && (
        <div className="mt-6">
          <h2 className="mb-4 text-lg font-semibold">Sadakat Programı</h2>
          <Card>
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">{data.loyaltyProgram.name}</span>
                <Badge variant={data.loyaltyProgram.isActive ? "default" : "secondary"}>
                  {data.loyaltyProgram.isActive ? "Aktif" : "Pasif"}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                <span>Kapsam: {data.loyaltyProgram.scope === "all_venues" ? "Tüm işletmeler" : "Sadece bu işletme"}</span>
                <span className="ml-4">1 TL = {data.loyaltyProgram.pointsPerCurrency} puan</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loyalty Customers */}
      {data.loyaltyCustomers && data.loyaltyCustomers.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-4 text-lg font-semibold">Sadakat Müşterileri ({data.loyaltyCustomers.length})</h2>
          <Card>
            <CardContent className="pt-5">
              <div className="space-y-2">
                {data.loyaltyCustomers.map((lc) => (
                  <div key={lc._id} className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <p className="font-medium text-sm">{lc.customer?.name || lc.customer?.email || "Anonim"}</p>
                      {lc.customer?.email && <p className="text-xs text-muted-foreground">{lc.customer.email}</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">{lc.points} puan</p>
                      <p className="text-xs text-muted-foreground">
                        Kazanılan: {lc.totalEarned} | Harcanan: {lc.totalRedeemed}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Menus */}
      <div className="mt-6">
        <h2 className="mb-4 text-lg font-semibold">Menuler ({data.menus.length})</h2>
        {data.menus.length === 0 ? (
          <p className="text-sm text-muted-foreground">Henuz menu yok</p>
        ) : (
          <div className="space-y-4">
            {data.menus.map((menu) => (
              <Card key={menu._id}>
                <CardHeader>
                  <CardTitle className="text-base">{menu.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  {menu.categories.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Kategori yok</p>
                  ) : (
                    <div className="space-y-3">
                      {menu.categories.map((cat) => (
                        <div key={cat._id}>
                          <p className="mb-1 text-sm font-semibold text-muted-foreground">{cat.name}</p>
                          <div className="ml-4 space-y-1">
                            {cat.items.map((item) => (
                              <div key={item._id} className="flex justify-between text-sm">
                                <span>{item.name}</span>
                                <span className="font-medium">{item.price} TL</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
